import GlassCard from '@app/features/billing/components/BaseGlassCard/GlassCard';
import type { TranslationKeys } from '@app/i18n/translationKeys';
import { currencyFormatter, getCurrencySymbol, SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { Tooltip } from 'antd';
import { FiAlertCircle, FiInfo, FiTrendingUp } from 'react-icons/fi';
import { LuStethoscope } from 'react-icons/lu';
import { RiNurseFill } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import Text from '../../../../../components/ui/Text/Text';

type KpiResultsProps = {
  isLoading?: boolean;
  currency: SupportedCurrencies;
  totalAcquired: number;
  totalIncomeInvoiced: number;
  doctorPayables: number;
  caregiverPayables: number;
  openClientBalances: number;
  netCashPosition: number;
};

type KpiCardTitleProps = {
  titleKey: TranslationKeys;
  infoKey: TranslationKeys;
  formulaKey?: TranslationKeys;
};

const KpiCardTitle = ({ titleKey, infoKey, formulaKey }: KpiCardTitleProps) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5">
      <Text type="label1" transKey={titleKey} />

      <Tooltip
        placement="topLeft"
        trigger={['hover', 'focus']}
        mouseEnterDelay={0.1}
        styles={{
          root: { maxWidth: 340 },
          body: {
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            color: '#0f172a',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
            padding: '10px 12px',
          },
        }}
        title={
          <div className="space-y-2">
            <Text type="body2" transKey={infoKey} color="#0f172a" lh={1.45} />
            {formulaKey ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
                <Text type="code" transKey={formulaKey} color="#0f172a" lh={1.45} />
              </div>
            ) : null}
          </div>
        }
      >
        <span
          className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full text-slate-400 transition-colors hover:text-slate-600"
          aria-label={t('billing-kpi-more-info-aria')}
        >
          <FiInfo size={14} />
        </span>
      </Tooltip>
    </span>
  );
};

const getNetCashValueColorClassName = (netCashPosition: number): string => {
  if (netCashPosition > 0) {
    return 'text-emerald-800';
  }

  if (netCashPosition === 0) {
    return 'text-slate-500';
  }

  return 'text-rose-700';
};

const formatSignedCurrency = (value: number, currency: SupportedCurrencies): string => {
  const absoluteValueText = currencyFormatter(Math.abs(value), currency);

  if (value > 0) {
    return `+${absoluteValueText}`;
  }

  if (value < 0) {
    return `-${absoluteValueText}`;
  }

  return absoluteValueText;
};

const KpiResults = ({
  isLoading = false,
  currency,
  totalAcquired,
  totalIncomeInvoiced,
  doctorPayables,
  caregiverPayables,
  openClientBalances,
  netCashPosition,
}: KpiResultsProps) => {
  const netCashValueColorClassName = getNetCashValueColorClassName(netCashPosition);

  const invoiceAmountText = currencyFormatter(Math.abs(totalIncomeInvoiced), currency);
  const currencySymbol = getCurrencySymbol(currency);

  return (
    <section className="border-b border-slate-200 pb-10">
      <div className="grid grid-cols-12 gap-6">
        <GlassCard
          className="col-span-12 xl:col-span-4"
          theme="balanceAmberSoft"
          isLoading={isLoading}
          icon={<FiAlertCircle color="#f59e0b" />}
          iconBgColor="#ffffff"
          title={
            <KpiCardTitle
              titleKey="billing-overview-kpi-open-client-balances-title"
              infoKey="billing-overview-kpi-open-client-balances-info-tooltip"
            />
          }
          body={<Text type="h2" untranslatedText={currencyFormatter(Math.abs(openClientBalances), currency)} />}
        />

        <GlassCard
          className="col-span-12 xl:col-span-4"
          theme="payables"
          isLoading={isLoading}
          icon={<LuStethoscope color="#94a3b8" />}
          iconBgColor="#ffffff"
          title={
            <KpiCardTitle
              titleKey="billing-overview-kpi-doctor-payables-title"
              infoKey="billing-overview-kpi-doctor-payables-info-tooltip"
            />
          }
          description={
            <Text type="body2" transKey="billing-overview-kpi-doctor-payables-description" color="#64748b" />
          }
          body={<Text type="h2" untranslatedText={currencyFormatter(Math.abs(doctorPayables), currency)} />}
        />

        <GlassCard
          className="col-span-12 xl:col-span-4"
          theme="payables"
          isLoading={isLoading}
          icon={<RiNurseFill color="#7dd3fc" />}
          iconBgColor="#ffffff"
          title={
            <KpiCardTitle
              titleKey="caregiver-payables"
              infoKey="billing-overview-kpi-caregiver-payables-info-tooltip"
            />
          }
          description={
            <Text type="body2" transKey="billing-overview-kpi-caregiver-payables-description" color="#64748b" />
          }
          body={<Text type="h2" untranslatedText={currencyFormatter(Math.abs(caregiverPayables), currency)} />}
        />

        <GlassCard
          className="col-span-12 xl:col-span-6"
          theme="income"
          isLoading={isLoading}
          icon={<span className="text-[18px] leading-none text-emerald-500">€</span>}
          iconBgColor="#ffffff"
          title={
            <KpiCardTitle
              titleKey="billing-overview-kpi-income-title"
              infoKey="billing-overview-kpi-income-info-tooltip"
            />
          }
          description={
            <Text
              type="body2"
              transKey="billing-overview-kpi-income-description"
              transVariables={{ amount: invoiceAmountText, currencySymbol }}
              color="#64748b"
            />
          }
          body={<Text type="h2" untranslatedText={currencyFormatter(Math.abs(totalAcquired), currency)} />}
        />

        <GlassCard
          className="col-span-12 xl:col-span-6"
          theme="success"
          isLoading={isLoading}
          icon={<FiTrendingUp color="#16a34a" />}
          iconBgColor="#ffffff"
          title={
            <KpiCardTitle
              titleKey="billing-overview-kpi-net-cash-position-title"
              infoKey="billing-overview-kpi-net-cash-position-info-tooltip"
              formulaKey="billing-overview-kpi-net-cash-position-formula"
            />
          }
          body={
            <Text
              type="h2"
              untranslatedText={formatSignedCurrency(netCashPosition, currency)}
              className={netCashValueColorClassName}
            />
          }
        />
      </div>
    </section>
  );
};

export default KpiResults;
